import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import Loader from 'components/Common/Loader/Loader';
import useColissimoToken from './colissimo-token.hook';
import { useCustomer } from 'hooks/useCustomer';
import { RelayPoint } from 'types/Cart';

import './colissimo.scss';

type FrameColissimoOpenType = (params: {
  ceCountryList?: string;
  ceCountry: string;
  ceLang?: string;
  dyPreparationTime?: string;
  ceAddress?: string;
  ceZipCode?: string;
  ceTown?: string;
  dyWeight?: string;
  callBackFrame?: string;
  URLColissimo?: string;
  token: string;
}) => void;

type WidgetContainerType =
  | {
      frameColissimoOpen?: FrameColissimoOpenType;
      frameColissimoClose?: () => WidgetContainerType;
    }
  | undefined;

type PropsType = {
  onSelect: (point: RelayPoint) => void;
  onClose?: () => void;
  classes?: {
    container: string;
  };
};

export type RefType = {
  closeColissimo: () => void;
};

function ColissimoPlugin(
  { onSelect, onClose, classes }: PropsType,
  ref: ForwardedRef<RefType>
): JSX.Element {
  const { primaryAddress } = useCustomer();
  const widget = useRef<HTMLDivElement>(null);
  const { isLoaded, token } = useColissimoToken();
  const refFunctions = () => {
    return {
      closeColissimo: () => {
        const el = widget.current;
        if (el) {
          const jQueryFunction = window['jQuery'];
          const $widgetContainer: WidgetContainerType =
            jQueryFunction(el) || undefined;
          if (typeof $widgetContainer?.frameColissimoClose === 'function') {
            $widgetContainer.frameColissimoClose();
          }
        }
      },
    };
  };

  useImperativeHandle(ref, refFunctions, [widget]);

  useEffect(() => {
    function clickHandler(e: MouseEvent) {
      const closeClass = 'widget_colissimo_close';

      if ((e.target as Element).classList.contains(closeClass) && onClose) {
        onClose();
      }
    }

    if (widget.current && token) {
      const el = widget.current;
      const jQueryFunction = window['jQuery'];
      const $document = jQueryFunction(document) || undefined;
      const $widgetContainer: WidgetContainerType =
        jQueryFunction(el) || undefined;

      $document?.on(
        'ajaxComplete.colissimoListAjax',
        (event: unknown, xhr: unknown, settings: { url: string }) => {
          const findPath = '/widget-colissimo/rest/GetPointsRetraitGET.rest';
          if ((settings?.url || '').includes(findPath)) {
            const list = el.querySelector('#widget_colissimo_liste');

            if (list) {
              list.scrollTop = 0;
            }
          }
        }
      );

      if (typeof $widgetContainer?.frameColissimoOpen === 'function') {
        window['colissimoCallback'] = (point: RelayPoint) => {
          onSelect(point);

          if (typeof $widgetContainer?.frameColissimoClose === 'function') {
            $widgetContainer.frameColissimoClose();
          }
        };

        $widgetContainer.frameColissimoOpen({
          ceLang: 'fr',
          callBackFrame: 'colissimoCallback',
          URLColissimo: 'https://ws.colissimo.fr',
          ceCountryList: 'FR,ES,GB,PT,DE',
          ceCountry: 'FR',
          dyPreparationTime: '1',
          ceAddress: primaryAddress?.street,
          ceZipCode: primaryAddress?.postalCode,
          ceTown: primaryAddress?.city,
          token,
        });

        el.addEventListener('click', clickHandler);
      }
    }

    return () => {
      const el = widget.current;
      const jQueryFunction = window['jQuery'];
      const $document = jQueryFunction(document) || undefined;

      $document?.off('.colissimoListAjax');

      if (el) {
        el.removeEventListener('click', clickHandler);

        const $widgetContainer: WidgetContainerType = jQueryFunction(el);

        if (typeof $widgetContainer?.frameColissimoClose === 'function') {
          $widgetContainer.frameColissimoClose();
        }
      }
    };
  }, [widget, token, primaryAddress]);

  const mutationObserverCallback = (mutations: MutationRecord[]) => {
    const autoCompletionId = 'widget_colissimo_autocomplete';
    const containerSelector = '#widget_colissimo_contenu';
    const inputAddressSelector = '#widget_colissimo_adresse';

    mutations.forEach(mutation => {
      if (
        mutation.type === 'childList' &&
        (mutation.target as Element).id === autoCompletionId
      ) {
        const autoCompletionEl = mutation.target as HTMLElement;
        const container = autoCompletionEl.closest(containerSelector);
        const input = container?.querySelector(inputAddressSelector);
        const inputBottomPosition = input?.getBoundingClientRect().bottom;
        const inputLeftPosition = input?.getBoundingClientRect().left;
        const containerTopPosition = container?.getBoundingClientRect().top;
        const containerLeftPosition = container?.getBoundingClientRect().left;

        if (
          undefined != inputBottomPosition &&
          undefined !== inputLeftPosition &&
          undefined !== containerTopPosition &&
          undefined !== containerLeftPosition
        ) {
          autoCompletionEl.style.setProperty(
            '--topPosition',
            `${inputBottomPosition - containerTopPosition}px`
          );

          autoCompletionEl.style.setProperty(
            '--leftPosition',
            `${inputLeftPosition - containerLeftPosition}px`
          );
        }

        if (input) {
          const isVisible = autoCompletionEl.children.length;
          input?.classList[isVisible ? 'add' : 'remove']('autoCompleteVisible');
        }
      }
    });
  };

  useEffect(() => {
    const observer = new MutationObserver(mutationObserverCallback);
    if (widget.current) {
      observer.observe(widget.current, {
        attributes: false,
        childList: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [widget]);

  return (
    <div
      id="colissimo-widget-container"
      ref={widget}
      className={classes?.container}
    >
      {!isLoaded && (
        <div className={'loader-wrapper-fixed'}>
          <div className={'loader-wrapper'}>
            <Loader />
          </div>
        </div>
      )}
    </div>
  );
}

export default forwardRef(ColissimoPlugin);
